import { Button, SidePanel } from '../../components/ui';

// Define the settings interface
interface ForceSettings {
  linkDistance: number;
  attractionStrength: number;
  chargeStrength: number;
  nodeSize: number;
  zoomLevel: number;
  particleSpeed: number;
  particleCount: number;
}

interface GraphSettingsPanelProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  settings: ForceSettings;
  onSettingsChange: (setting: keyof ForceSettings, value: number) => void;
  onReset: () => void;
}

interface SliderRowProps {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}

function SliderRow({ label, value, display, min, max, step = 1, onChange }: SliderRowProps) {
  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
        <span className="t-eyebrow">{label}</span>
        <span
          className="t-num"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}
        >
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: 'var(--accent)', height: '4px' }}
      />
    </div>
  );
}

export default function GraphSettingsPanel({
  isOpen,
  setIsOpen,
  settings,
  onSettingsChange,
  onReset
}: GraphSettingsPanelProps) {
  return (
    <SidePanel
      open={isOpen}
      onClose={() => setIsOpen(false)}
      width={360}
      eyebrow="Private Network"
      title="Graph Settings"
      footer={
        <>
          <Button variant="secondary" icon="rotate-ccw" onClick={onReset}>
            Reset to Default
          </Button>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        <SliderRow
          label="Link Distance"
          value={settings.linkDistance}
          display={String(settings.linkDistance)}
          min={20}
          max={300}
          onChange={(v) => onSettingsChange('linkDistance', Math.round(v))}
        />

        <SliderRow
          label="Attraction Strength"
          value={settings.attractionStrength}
          display={settings.attractionStrength.toFixed(2)}
          min={0}
          max={1}
          step={0.01}
          onChange={(v) => onSettingsChange('attractionStrength', v)}
        />

        <SliderRow
          label="Repulsion Strength"
          value={Math.abs(settings.chargeStrength)}
          display={String(Math.abs(settings.chargeStrength))}
          min={20}
          max={500}
          onChange={(v) => onSettingsChange('chargeStrength', -Math.round(v))}
        />

        <SliderRow
          label="Node Size"
          value={settings.nodeSize}
          display={String(settings.nodeSize)}
          min={2}
          max={15}
          step={0.5}
          onChange={(v) => onSettingsChange('nodeSize', v)}
        />

        <SliderRow
          label="Zoom Level"
          value={settings.zoomLevel}
          display={settings.zoomLevel.toFixed(1)}
          min={1}
          max={10}
          step={0.1}
          onChange={(v) => onSettingsChange('zoomLevel', v)}
        />

        <SliderRow
          label="Particle Count"
          value={settings.particleCount}
          display={String(settings.particleCount)}
          min={0}
          max={8}
          onChange={(v) => onSettingsChange('particleCount', Math.round(v))}
        />

        <SliderRow
          label="Particle Speed"
          value={settings.particleSpeed}
          display={settings.particleSpeed.toFixed(3)}
          min={0.001}
          max={0.02}
          step={0.001}
          onChange={(v) => onSettingsChange('particleSpeed', v)}
        />
      </div>
    </SidePanel>
  );
}
